import { useMemo, useState } from "react"
import { Link } from "react-router"
import { AnimatePresence, motion } from "framer-motion"
import { Home } from "lucide-react"
import { cn } from "@/lib/utils"
import type { RoomType } from "@/types/schedule"
import { MOCK_ROOMS } from "./mock-rooms"
import { RoomListCard } from "./RoomListCard"

// ─── Bộ lọc theo loại phòng ──────────────────────────────────────
type TypeFilter = "all" | "standard" | "vip"

const TYPE_FILTERS: { value: TypeFilter; label: string }[] = [
    { value: "all", label: "Tất cả" },
    { value: "standard", label: "Standard" },
    { value: "vip", label: "VIP" },
]

/** Kiểm tra phòng có khớp bộ lọc loại phòng không (VIP gồm cả supervip) */
function matchesTypeFilter(type: RoomType, filter: TypeFilter): boolean {
    if (filter === "all") return true
    if (filter === "vip") return type === "vip" || type === "supervip"
    return type === filter
}

export const RestRoomPage = () => {
    const [typeFilter, setTypeFilter] = useState<TypeFilter>("all")

    const filteredRooms = useMemo(
        () => MOCK_ROOMS.filter((r) => matchesTypeFilter(r.type, typeFilter)),
        [typeFilter],
    )

    return (
        <div>
            <RestRoomHero typeFilter={typeFilter} onTypeChange={setTypeFilter} />

            <div className="mx-auto max-w-[1200px] px-4 pb-20 pt-10 sm:px-8">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={typeFilter}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                    >
                        {filteredRooms.length > 0 ? (
                            <motion.div
                                className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
                                initial="hidden"
                                animate="visible"
                                variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
                            >
                                {filteredRooms.map((room) => (
                                    <RoomListCard key={room.id} room={room} />
                                ))}
                            </motion.div>
                        ) : (
                            <div className="flex justify-center py-12">
                                <p className="text-sm text-muted-foreground">Không có phòng phù hợp bộ lọc</p>
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    )
}

// ─── Hero + bộ lọc loại phòng ────────────────────────────────────
interface RestRoomHeroProps {
    typeFilter: TypeFilter
    onTypeChange: (value: TypeFilter) => void
}

function RestRoomHero({ typeFilter, onTypeChange }: RestRoomHeroProps) {
    return (
        <div className="relative">
            <section className="overflow-hidden border-b border-white/10 bg-[linear-gradient(105deg,#3F3323_0%,#5A4830_55%,#806C48_100%)] px-5 pb-28 pt-14 text-center text-[#f6f0e7] sm:pt-16">
                <nav
                    aria-label="Breadcrumb"
                    className="flex items-center justify-center gap-2 text-sm text-[#ead4ad] sm:text-base"
                >
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d5b46f]"
                    >
                        <Home className="size-4 fill-current" aria-hidden="true" />
                        Trang chủ
                    </Link>
                    <span className="text-[#d0a85e]" aria-hidden="true">›</span>
                    <span className="font-medium text-white">Danh sách phòng</span>
                </nav>

                <h1 className="mt-7 font-serif text-4xl font-semibold leading-none tracking-tight sm:text-5xl lg:text-[56px]">
                    Phòng nghỉ
                </h1>
                <p className="mt-5 text-lg font-medium text-[#f0c982] sm:text-xl">
                    Khám phá các loại phòng được thiết kế tinh tế, đầy đủ tiện nghi
                </p>
            </section>

            {/* Thẻ bộ lọc đè lên đáy hero */}
            <div className="mx-auto -mt-14 max-w-4xl px-4">
                <div className="flex flex-wrap items-center justify-center gap-3 rounded-2xl bg-white px-6 py-5 shadow-[0_10px_40px_-12px_rgba(63,51,35,0.35)]">
                    {TYPE_FILTERS.map((f) => {
                        const active = typeFilter === f.value
                        return (
                            <button
                                key={f.value}
                                type="button"
                                onClick={() => onTypeChange(f.value)}
                                className={cn(
                                    "rounded-lg px-5 py-2 text-sm font-semibold transition-colors",
                                    active
                                        ? "bg-[#8a7452] text-white shadow-sm"
                                        : "bg-[#f3f1ec] text-[#6f6555] hover:bg-[#e8e3d8]",
                                )}
                            >
                                {f.label}
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
